import { useEffect, useRef } from 'react';
import { usePdfStore } from '@/stores/pdfStore';
import { renderThumbnailDataUrl } from '@/lib/pdf/pdfDocument';

export default function PdfPageThumbs() {
  const pdfProxy = usePdfStore((s) => s.pdfProxy);
  const pageCount = usePdfStore((s) => s.pageCount);
  const activePageIndex = usePdfStore((s) => s.activePageIndex);
  const thumbUrls = usePdfStore((s) => s.thumbUrls);
  const setActivePage = usePdfStore((s) => s.setActivePage);
  const setThumbUrl = usePdfStore((s) => s.setThumbUrl);
  const loadingRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!pdfProxy || pageCount === 0) return;
    let cancelled = false;

    const loadNearby = async () => {
      const order = [
        activePageIndex,
        activePageIndex - 1,
        activePageIndex + 1,
        ...Array.from({ length: pageCount }, (_, i) => i),
      ];
      const seen = new Set<number>();

      for (const idx of order) {
        if (cancelled) break;
        if (idx < 0 || idx >= pageCount || seen.has(idx)) continue;
        seen.add(idx);
        if (thumbUrls[idx] || loadingRef.current.has(idx)) continue;
        loadingRef.current.add(idx);
        try {
          const url = await renderThumbnailDataUrl(pdfProxy, idx + 1);
          if (!cancelled) setThumbUrl(idx, url);
        } catch (err) {
          console.warn('Thumb render failed', idx, err);
        } finally {
          loadingRef.current.delete(idx);
        }
      }
    };

    void loadNearby();
    return () => {
      cancelled = true;
    };
  }, [pdfProxy, pageCount, activePageIndex, thumbUrls, setThumbUrl]);

  return (
    <aside className="pdfe-thumbs">
      <div className="pdfe-thumbs-head">
        <span>Pages</span>
      </div>
      <ul className="pdfe-thumbs-list">
        {Array.from({ length: pageCount }, (_, i) => (
          <li key={i}>
            <button
              type="button"
              className={`pdfe-thumb${i === activePageIndex ? ' is-active' : ''}`}
              onClick={() => setActivePage(i)}
              aria-label={`Page ${i + 1}`}
              aria-current={i === activePageIndex ? 'page' : undefined}
            >
              {thumbUrls[i] ? (
                <img src={thumbUrls[i]} alt="" />
              ) : (
                <span className="pdfe-thumb-placeholder">…</span>
              )}
              <span className="pdfe-thumb-label">Page {i + 1}</span>
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}
